// AICA Sheet Component - Drawer/Slider based on Radix Dialog
// Used for detail views and side panels
//
// Usage:
// <Sheet>
//   <SheetTrigger>Open</SheetTrigger>
//   <SheetContent>
//     <SheetHeader>
//       <SheetTitle>Title</SheetTitle>
//       <SheetDescription>Description</SheetDescription>
//     </SheetHeader>
//     <SheetBody>
//       {/* Main content with proper padding */}
//     </SheetBody>
//     <SheetFooter>
//       {/* Footer actions */}
//     </SheetFooter>
//   </SheetContent>
// </Sheet>

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { CONTROL_SQUARE } from '@/components/ui/control-size';

const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetClose = DialogPrimitive.Close;
const SheetPortal = DialogPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-modal bg-bg-void/50 backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface SheetContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  side?: 'top' | 'bottom' | 'left' | 'right';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

const sheetVariants = {
  top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
  bottom:
    'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
  left: 'inset-y-0 left-0 h-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
  right:
    'inset-y-0 right-0 h-full border-r data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
};

const sizeVariants = {
  sm: 'w-[90%] sm:max-w-sm', // 384px on desktop
  md: 'w-[90%] sm:max-w-md', // 448px
  lg: 'w-[90%] sm:max-w-lg', // 512px
  xl: 'w-[90%] sm:max-w-2xl', // 672px
  full: 'w-full sm:max-w-4xl', // 896px
};

const SheetContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ side = 'right', size = 'xl', className, children, ...props }, ref) => {
  const { t } = useTranslation();

  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Content
        ref={ref}
        // A side drawer is as tall as the part of a content-sized embedded frame the portal shows; a top or
        // bottom sheet sits at that edge of it (index.css).
        data-frame-anchor={side === 'left' || side === 'right' ? 'fill' : side}
        className={cn(
          'fixed z-modal flex flex-col bg-bg border-border shadow-lg',
          // Bloomberg-style animations: moderate duration (240ms)
          'transition-all ease-decelerate',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:duration-moderate data-[state=open]:duration-moderate',
          // Reduced motion support
          'motion-reduce:transition-none',
          sheetVariants[side],
          (side === 'left' || side === 'right') && sizeVariants[size],
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close
          className={cn(
            CONTROL_SQUARE.sm,
            'absolute right-3 top-3 sm:right-4 sm:top-4 rounded-sm opacity-70 ring-offset-bg transition-opacity duration-150 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-bg-elevated flex items-center justify-center'
          )}
        >
          <X className="h-5 w-5 text-fg-muted" />
          <span className="sr-only">{t('common:common.close')}</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = DialogPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-2 text-left border-b border-border',
      'p-4 sm:p-5 md:p-6', // Responsive padding
      className
    )}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex-1 overflow-y-auto',
      'p-4 sm:p-5 md:p-6', // Responsive padding matching header/footer
      className
    )}
    {...props}
  />
);
SheetBody.displayName = 'SheetBody';

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 border-t border-border',
      'p-4 sm:p-5 md:p-6', // Responsive padding
      className
    )}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-base font-medium text-fg', className)}
    {...props}
  />
));
SheetTitle.displayName = DialogPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-fg-muted', className)}
    {...props}
  />
));
SheetDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
